import React from 'react';
import styled from 'styled-components';
import { Trans, useTranslation } from 'react-i18next';
import { themeColor } from '../Theming';
import {
  navigateToAndroidSettings,
  onClose,
} from '../Common/KindredAndroidInterface/KindredAndroidInterface';
import {
  ANALYTICS_PAGE_NAMES,
  trackClickedActionEvent,
} from '../Common/Analytics/utils';
import { usePageViewTracking, useQuery } from '../Common/Hooks';
import Header from './components/Header';
import { normalizeText } from '../EonPluginActivation/utils';
import ActivateNow from './components/ActivateNow';

const Container = styled.main`
  flex-direction: column;
  display: flex;
  flex: 1;
  max-width: 600px;
  margin: 0 auto;
`;

const BottomContent = styled.section`
  flex-direction: column;
  padding-bottom: 24px;
`;

const DisclaimerContainer = styled.div`
  background-color: ${themeColor('lightGreyBackground')};
  margin-inline: 30px;
  margin-top: -120px;
  border-radius: 10px;
  padding: 10px;
  overflow: auto;
  max-height: 23vh;
  flex-grow: 1;
  font-family: Helvetica;
  color: ${themeColor('black')};
  font-weight: normal;
  box-shadow: 0px 0px 8px 2px rgba(0, 0, 0, 0.1);
  position: relative;
  z-index: 1;
`;

const DisclaimerParagraph = styled.p`
  font-family: Helvetica;
  color: ${themeColor('black')};
  font-weight: normal;
  margin: 0;
  padding-bottom: 5px;
  font-size: 15px;
  line-height: normal;

  & > a {
    color: ${themeColor('black')};
    text-decoration: underline;
`;

const DisclaimerHeading = styled.p`
  color: ${themeColor('black')};
  font-weight: normal;
  margin: 0 0 5px 0;
  padding: 0;
  font-size: 15px;
  line-height: normal;
`;

const NTVPluginActivationAndroidAccessibility: React.FC = () => {
  const { t } = useTranslation();
  const query = useQuery();
  const { accessibility_instructions_page } = ANALYTICS_PAGE_NAMES;
  usePageViewTracking(accessibility_instructions_page, 'NTV');

  const renderContent = () => {
    return (
      <Container>
        <Header
          logoWidth={51}
          height={270}
          androidAccessibility={true}
          text={
            <>
              <span>Bedienungshilfen</span>
              <span>aktivieren</span>
            </>
          }
        />
        <DisclaimerContainer tabIndex={0}>
          <DisclaimerParagraph>
            Zunächst muss in den Geräteeinstellungen unter &quot;Apps&quot; für
            ntv ein uneingeschränkter Zugriff aktiviert werden. Anschließend
            kann in den Eingabehilfen unter &quot;installierte Apps&quot;
            &quot;ntv Shopping Deals&quot; eingeschaltet werden. Durch die
            Aktivierung erteilen Sie unserer ntv-Erweiterung die Berechtigung,
            auf Informationen zu den auf Ihrem Gerät installierten Apps
            zuzugreifen. Wir verwenden diese Berechtigung speziell, um zu
            erkennen, wann Sie eine Browser-App (z. B. Chrome) verwenden, damit
            wir Ihnen nur dann Pop-ups mit Rabattcodes anzeigen, wenn Sie online
            im Browser einkaufen.
          </DisclaimerParagraph>

          <DisclaimerHeading>
            Für folgende Zwecke verwenden wir die Berechtigung:
          </DisclaimerHeading>

          <DisclaimerParagraph>
            - Um auf eine Liste der installierten und aktiven Apps zuzugreifen,
            damit wir erkennen können, wann Sie eine Browser-App für
            Online-Einkäufe verwenden.
          </DisclaimerParagraph>

          <DisclaimerParagraph>
            - Um auf Ihren Webbrowser-Verlauf zuzugreifen und Ihnen relevante
            Angebote anzuzeigen.
          </DisclaimerParagraph>

          <DisclaimerParagraph>
            - Um eine anonymisierte Klick-ID (unter Verwendung von Geräte- oder
            anderen Identifikatoren) zu erstellen, um Käufe mit angezeigten
            Angeboten zu verknüpfen.
          </DisclaimerParagraph>

          <DisclaimerParagraph>
            Gegebenenfalls geben wir den Standort Ihres Geräts und die Domains
            der von Ihnen besuchten Websites an Dritte weiter, um im Rahmen der
            ntv-Funktion gültige Gutscheine und Angebote abzurufen und
            anzuzeigen.
          </DisclaimerParagraph>
        </DisclaimerContainer>
        <BottomContent>
          <ActivateNow
            navigateToSettings={() => {
              navigateToAndroidSettings();
              trackClickedActionEvent(
                query,
                'NTV',
                'agree',
                accessibility_instructions_page
              );
            }}
            close={() => {
              onClose?.();
              trackClickedActionEvent?.(
                query,
                'NTV',
                'disagree',
                accessibility_instructions_page
              );
            }}
            title={'Das müssen Sie dafür tun:'}
            steps={[
              'Klicken Sie “Zustimmen” um die <strong>Einstellungen</strong> zu öffnen',
              'Klicken Sie <strong>“Installierte Apps”</strong> um “ntv Shopping Deals” zu finden',
              '<strong>Aktivieren</strong> Sie “ntv Shopping Deals”',
            ]}
          />
        </BottomContent>
      </Container>
    );
  };

  return renderContent();
};

export default NTVPluginActivationAndroidAccessibility;
